import React from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Button from "@/components/Button";
import { Search, Plus, User, List, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { usePageTracking } from "@/hooks/usePageTracking";
import FeaturedAmazonProducts from "@/components/FeaturedAmazonProducts";
import SEO from "@/components/SEO";


const Home = () => {
  usePageTracking(); // Track page views
  const navigate = useNavigate();
  const [showPersonalPromoDialog, setShowPersonalPromoDialog] = React.useState(false);
  
  const handlePreviewTemplate = () => {
    window.open("https://docs.google.com/spreadsheets/d/1gMp_MVPykMKaW_smczFiiFuPc2TMX5kW8rsSAQzGgVE/edit?usp=sharing", "_blank", "noopener,noreferrer");
  };
  const handleSaveTemplate = () => {
    window.open("https://docs.google.com/spreadsheets/d/1gMp_MVPykMKaW_smczFiiFuPc2TMX5kW8rsSAQzGgVE/copy", "_blank", "noopener,noreferrer");
  };
  const handlePersonalPromoClick = () => {
    setShowPersonalPromoDialog(true);
  };
  const handleGoToSheet = () => {
    window.open("https://sheets.google.com", "_blank", "noopener,noreferrer");
  };
  return <Layout>
      <SEO title="Cashback e Promozioni Italia" description="Scopri cashback, rimborsi e promozioni nella tua città. Condividi offerte locali e trova sconti esclusivi in tutta Italia. Risparmia con AffareFatto!" keywords="promozioni, offerte, sconti, cashback, rimborsi, Italia, promozioni locali, risparmio, shopping, codici sconto" structuredData={[
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "AffareFatto",
          "alternateName": "Affare Fatto",
          "url": "https://www.affarefatto.info",
          "description": "Piattaforma italiana per scoprire e condividere cashback, rimborsi e promozioni locali.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://www.affarefatto.info/search?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          },
          "publisher": {
            "@type": "Organization",
            "name": "AffareFatto",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.affarefatto.info/favicon.ico"
            }
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "AffareFatto",
          "description": "Piattaforma italiana per cashback e promozioni locali",
          "url": "https://www.affarefatto.info",
          "email": "affarefatto.help@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Via Salerno 11",
            "addressLocality": "Santarcangelo di Romagna",
            "addressRegion": "RN",
            "postalCode": "47822",
            "addressCountry": "IT"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "44.0627",
            "longitude": "12.4472"
          },
          "areaServed": {
            "@type": "Country",
            "name": "Italia"
          },
          "priceRange": "Gratis",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "00:00",
            "closes": "23:59"
          },
          "sameAs": [
            "https://www.facebook.com/groups/affarefatto.info",
            "https://www.instagram.com/affarefatto.info/",
            "https://www.youtube.com/@AffareFattoinfo"
          ]
        }
      ]} />
      <div className="min-h-[calc(100vh-200px)] flex flex-col items-center px-2 sm:px-4 py-4">
        {/* Main content with fixed min-height to prevent CLS */}
        <div className="w-full max-w-sm flex flex-col justify-center space-y-3 sm:space-y-4 mt-[19px] min-h-[450px]">
          {/* H1 per SEO - visually styled as welcome message */}
          <div className="bg-white border-2 border-dashed border-black rounded-xl p-4 sm:p-6 shadow-md">
            <h1 className="text-base sm:text-lg text-gray-800 font-medium text-center">
              💰 Cashback e Rimborsi Italia 🔍
            </h1>
            <p className="text-base sm:text-lg text-gray-800 font-medium text-center">
              🥇Il primo sito aggiornato in tempo reale
            </p>
            <p className="text-base sm:text-lg text-gray-800 font-medium text-center">
              con l'aiuto della community!📍
            </p>
          </div>

          {/* Bottoni principali */}
          <div className="space-y-3 sm:space-y-4 flex flex-col justify-center">
            <Button onClick={() => navigate("/promo-list")} variant="primary" size="lg" fullWidth className="h-14 sm:h-16 text-lg sm:text-xl font-bold flex items-center justify-center gap-2 shadow-lg bg-[hsl(var(--blue-pastel))] hover:bg-[hsl(var(--blue-pastel))]/90 text-black border-2 border-black hover:animate-pulse transition-all duration-300">
              <List className="h-6 w-6 sm:h-7 sm:w-7" />
              <span className="flex flex-col items-center leading-tight">
                <span>PROMO</span>
                <span className="text-[10px] sm:text-xs font-normal">CASHBACK</span>
              </span>
            </Button>
            
            <Button onClick={() => navigate("/search")} variant="primary" size="lg" fullWidth className="h-14 sm:h-16 text-lg sm:text-xl font-bold flex items-center justify-center gap-2 shadow-lg hover:animate-pulse transition-all duration-300">
              <Search className="h-6 w-6 sm:h-7 sm:w-7" />
              <span className="flex flex-col items-center leading-tight">
                <span>CERCA PROMO</span>
                <span className="text-[10px] sm:text-xs font-normal">CASHBACK</span>
              </span>
            </Button>
            
            <Button onClick={() => navigate("/add")} variant="secondary" size="lg" fullWidth className="h-14 sm:h-16 text-lg sm:text-xl font-bold flex items-center justify-center gap-2 shadow-lg hover:animate-pulse transition-all duration-300">
              <Plus className="h-6 w-6 sm:h-7 sm:w-7" />
              <span className="flex flex-col items-center leading-tight">
                <span>AGGIUNGI PROMO</span>
                <span className="text-[10px] sm:text-xs font-normal">CASHBACK</span>
              </span>
            </Button>
            
            <Button onClick={handlePersonalPromoClick} variant="accent" size="lg" fullWidth className="h-14 sm:h-16 text-lg sm:text-xl font-bold flex items-center justify-center gap-2 shadow-lg hover:animate-pulse transition-all duration-300">
              <User className="h-6 w-6 sm:h-7 sm:w-7" />
              <span className="flex flex-col items-center leading-tight">
                <span>REGISTRA PROMO</span>
                <span className="text-[10px] sm:text-xs font-normal">PERSONALE</span>
              </span>
            </Button>
            
            <Button 
              onClick={() => navigate("/omaggi-concorsi")} 
              variant="primary" 
              size="lg" 
              fullWidth 
              className="h-14 sm:h-16 text-lg sm:text-xl font-bold flex items-center justify-center gap-2 shadow-lg bg-[hsl(var(--orange-pastel))] hover:bg-[hsl(var(--orange-pastel))]/90 text-black border-2 border-black hover:animate-pulse transition-all duration-300"
              aria-label="Sezione Omaggi e Concorsi"
            >
              <Tag className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
              <span className="flex flex-col items-center leading-tight">
                <span>OMAGGI E CONCORSI</span>
                <span className="text-[10px] sm:text-xs font-normal">E ALTRO</span>
              </span>
            </Button>
          </div>
        </div>


        {/* Vetrina Super Offerte Amazon - Full width */}
        <FeaturedAmazonProducts />
        
      </div>




      {/* Dialog per Registra Promo Personale */}
      <Dialog open={showPersonalPromoDialog} onOpenChange={setShowPersonalPromoDialog}>
        <DialogContent className="w-[90vw] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold flex items-center justify-center gap-2">
              <span>👤</span>
              <span>Registra Promo Personale</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-6">
            {/* Prima scheda - Salva il Template */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-center">
                <h3 className="text-lg font-semibold flex items-center justify-center gap-2">
                  <span>💾</span>
                  <span>Salva il Template</span>
                </h3>
              </div>
              <p className="text-sm text-gray-600 text-center">Da cliccare SOLO la 1ª volta. Salva il Template nel tuo account gmail su Google Sheets</p>
              <Button onClick={handlePreviewTemplate} variant="primary" size="md" fullWidth className="mt-3">
                Anteprima Template
              </Button>
              <Button onClick={handleSaveTemplate} variant="primary" size="md" fullWidth className="mt-2">
                Salva Template
              </Button>
            </div>

            {/* Seconda scheda - Vai al Tuo Foglio */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-center">
                <h3 className="text-lg font-semibold flex items-center justify-center gap-2">
                  <span>📄</span>
                  <span>Vai al Tuo Foglio</span>
                </h3>
              </div>
              <p className="text-sm text-gray-600 text-center">Hai già salvato il foglio di Google Sheets,qui puoi accederei aggiornare le tue PROMO</p>
              <Button onClick={handleGoToSheet} variant="secondary" size="md" fullWidth className="mt-3">
                Apri Google Sheets
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>;
};
export default Home;
